// Import all necessary types and utilities
import { Building, Floor, Apartment, KonzernId } from '../types/building';
import { User, UserPermissions } from '../types/user';
import { addAuditLog } from './auditLog';
import { encryptData, decryptData, hashPassword } from './encryption';

// Storage keys
const BUILDINGS_KEY = 'installtrack_buildings';
const ARCHIVED_BUILDINGS_KEY = 'installtrack_archived_buildings';
const USERS_KEY = 'installtrack_users';
const CURRENT_USER_KEY = 'installtrack_current_user';
const KONZERN_IDS_KEY = 'installtrack_konzern_ids';

// Default data
const DEFAULT_ADMIN: User = {
  id: crypto.randomUUID(),
  email: 'admin@example.com',
  password: hashPassword('Admin123'),
  name: 'Administrator',
  role: 'admin',
  company: 'InstallTrack GmbH',
  organizationId: 'default-org',
  lastLogin: new Date(),
  permissions: {
    canCreateBuildings: true,
    canEditBuildings: true,
    canDeleteBuildings: true,
    canManageTechnicians: true,
    canManageKonzernIds: true,
    canExportData: true,
    canImportData: true,
    canAccessApi: true,
    canViewAuditLogs: true,
    canManagePermissions: true
  }
};

// Initialize storage with default data if empty
const initializeLocalStorage = (): void => {
  try {
    if (!localStorage.getItem(BUILDINGS_KEY)) {
      localStorage.setItem(BUILDINGS_KEY, JSON.stringify([]));
    }
    
    if (!localStorage.getItem(ARCHIVED_BUILDINGS_KEY)) {
      localStorage.setItem(ARCHIVED_BUILDINGS_KEY, JSON.stringify([]));
    }
    
    if (!localStorage.getItem(USERS_KEY)) {
      localStorage.setItem(USERS_KEY, JSON.stringify([DEFAULT_ADMIN]));
    }

    if (!localStorage.getItem(KONZERN_IDS_KEY)) {
      localStorage.setItem(KONZERN_IDS_KEY, JSON.stringify([]));
    }
  } catch (error) {
    console.error('Error initializing local storage:', error);
  }
};

// User management
export const getUsers = (): User[] => {
  try {
    const users = localStorage.getItem(USERS_KEY);
    if (!users) {
      // Initialize with default admin if no users exist
      const defaultUsers = [DEFAULT_ADMIN];
      localStorage.setItem(USERS_KEY, JSON.stringify(defaultUsers));
      return defaultUsers;
    }
    
    return JSON.parse(users, (key, value) => {
      if (key === 'lastLogin' && value) {
        return new Date(value);
      }
      return value;
    });
  } catch (error) {
    console.error('Error reading users from localStorage:', error);
    return [];
  }
};

export const saveUsers = (users: User[]): void => {
  try {
    localStorage.setItem(USERS_KEY, JSON.stringify(users));
  } catch (error) {
    console.error('Error saving users to localStorage:', error);
  }
};

export const getCurrentUser = (): User | null => {
  try {
    const userStr = localStorage.getItem(CURRENT_USER_KEY);
    if (!userStr) return null;
    
    return JSON.parse(userStr, (key, value) => {
      if (key === 'lastLogin' && value) {
        return new Date(value);
      }
      return value;
    });
  } catch (error) {
    console.error('Error reading current user from localStorage:', error);
    return null;
  }
};

export const setCurrentUser = (user: User): void => {
  try {
    localStorage.setItem(CURRENT_USER_KEY, JSON.stringify(user));
  } catch (error) {
    console.error('Error saving current user to localStorage:', error);
  }
};

export const clearCurrentUser = (): void => {
  try {
    localStorage.removeItem(CURRENT_USER_KEY);
  } catch (error) {
    console.error('Error clearing current user from localStorage:', error);
  }
};

export const updateUserPermissions = (userId: string, permissions: UserPermissions): boolean => {
  try {
    const users = getUsers();
    const userIndex = users.findIndex(u => u.id === userId);
    
    if (userIndex === -1) {
      throw new Error('Benutzer nicht gefunden');
    }
    
    // Update permissions
    users[userIndex] = {
      ...users[userIndex],
      permissions
    };
    
    saveUsers(users);
    
    // Update current user if it's the same user
    const currentUser = getCurrentUser();
    if (currentUser && currentUser.id === userId) {
      setCurrentUser({
        ...currentUser,
        permissions
      });
    }
    
    return true;
  } catch (error) {
    console.error('Error updating user permissions:', error);
    throw error;
  }
};

// Building management
export const getBuildings = (): Building[] => {
  try {
    const buildings = localStorage.getItem(BUILDINGS_KEY);
    if (!buildings) return [];
    
    return JSON.parse(buildings, (key, value) => {
      if (key === 'lastUpdated' && value) {
        return new Date(value);
      }
      return value;
    });
  } catch (error) {
    console.error('Error reading buildings from localStorage:', error);
    return [];
  }
};

export const getArchivedBuildings = (): Building[] => {
  try {
    const buildings = localStorage.getItem(ARCHIVED_BUILDINGS_KEY);
    if (!buildings) return [];
    
    return JSON.parse(buildings, (key, value) => {
      if (key === 'lastUpdated' && value) {
        return new Date(value);
      }
      return value;
    });
  } catch (error) {
    console.error('Error reading archived buildings from localStorage:', error);
    return [];
  }
};

const saveBuildings = (buildings: Building[]): void => {
  try {
    localStorage.setItem(BUILDINGS_KEY, JSON.stringify(buildings));
  } catch (error) {
    console.error('Error saving buildings to localStorage:', error);
  }
};

const saveArchivedBuildings = (buildings: Building[]): void => {
  try {
    localStorage.setItem(ARCHIVED_BUILDINGS_KEY, JSON.stringify(buildings));
  } catch (error) {
    console.error('Error saving archived buildings to localStorage:', error);
  }
};

export const getBuilding = (id: string, archived = false): Building | null => {
  try {
    const buildings = archived ? getArchivedBuildings() : getBuildings();
    return buildings.find(b => b.id === id) || null;
  } catch (error) {
    console.error('Error getting building:', error);
    return null;
  }
};

export const createBuilding = (name: string, address: string): Building => {
  try {
    if (!name || !address) {
      throw new Error('Gebäudename und Adresse sind erforderlich');
    }
    
    const currentUser = getCurrentUser();
    const organizationId = currentUser?.organizationId || 'default-org';
    
    const buildings = getBuildings();
    
    const newBuilding: Building = {
      id: crypto.randomUUID(),
      organizationId,
      name,
      address,
      floors: [],
      metadata: {
        status: 'planning',
        startDate: new Date()
      }
    };
    
    buildings.push(newBuilding);
    saveBuildings(buildings);
    
    // Log the creation
    addAuditLog('create', 'building', `Building created: ${name}`, newBuilding.id);
    
    return newBuilding;
  } catch (error) {
    console.error('Error creating building:', error);
    throw error;
  }
};

export const updateBuilding = (id: string, updates: Partial<Building>, archived = false): Building => {
  try {
    const buildings = archived ? getArchivedBuildings() : getBuildings();
    const buildingIndex = buildings.findIndex(b => b.id === id);
    
    if (buildingIndex === -1) {
      // If building doesn't exist, create a new one
      if (updates.name && updates.address) {
        return createBuilding(updates.name, updates.address);
      }
      throw new Error('Gebäude nicht gefunden');
    }
    
    const updatedBuilding = {
      ...buildings[buildingIndex],
      ...updates,
      // Preserve organization ID and floors if not provided
      organizationId: buildings[buildingIndex].organizationId,
      floors: updates.floors || buildings[buildingIndex].floors
    };
    
    buildings[buildingIndex] = updatedBuilding;
    
    if (archived) {
      saveArchivedBuildings(buildings);
    } else {
      saveBuildings(buildings);
    }
    
    // Log the update
    addAuditLog('update', 'building', `Building updated: ${updatedBuilding.name}`, id);
    
    return updatedBuilding;
  } catch (error) {
    console.error('Error updating building:', error);
    throw error;
  }
};

export const deleteBuilding = (id: string, archived = false): void => {
  try {
    if (archived) {
      const buildings = getArchivedBuildings();
      saveArchivedBuildings(buildings.filter(b => b.id !== id));
    } else {
      const buildings = getBuildings();
      saveBuildings(buildings.filter(b => b.id !== id));
    }
  } catch (error) {
    console.error('Error deleting building:', error);
    throw error;
  }
};

export const archiveBuilding = (id: string): Building | null => {
  try {
    const buildings = getBuildings();
    const buildingIndex = buildings.findIndex(b => b.id === id);
    
    if (buildingIndex === -1) {
      throw new Error('Gebäude nicht gefunden');
    }
    
    const buildingToArchive = buildings[buildingIndex];
    
    // Add archival date
    const archivedBuilding = {
      ...buildingToArchive,
      archivedAt: new Date(),
      metadata: {
        ...buildingToArchive.metadata,
        status: 'archived' as const,
        actualCompletionDate: new Date()
      }
    };
    
    // Remove from active buildings
    buildings.splice(buildingIndex, 1);
    saveBuildings(buildings);
    
    // Add to archived buildings
    const archivedBuildings = getArchivedBuildings();
    archivedBuildings.push(archivedBuilding);
    saveArchivedBuildings(archivedBuildings);
    
    return archivedBuilding;
  } catch (error) {
    console.error('Error archiving building:', error);
    return null;
  }
};

export const unarchiveBuilding = (id: string): Building | null => {
  try {
    const archivedBuildings = getArchivedBuildings();
    const buildingIndex = archivedBuildings.findIndex(b => b.id === id);
    
    if (buildingIndex === -1) {
      throw new Error('Archiviertes Gebäude nicht gefunden');
    }
    
    const buildingToUnarchive = archivedBuildings[buildingIndex];
    
    // Remove archival date and update metadata
    const { archivedAt, ...buildingWithoutArchiveDate } = buildingToUnarchive;
    const unarchived = {
      ...buildingWithoutArchiveDate,
      metadata: {
        ...buildingWithoutArchiveDate.metadata,
        status: 'completed' as const
      }
    };
    
    // Remove from archived buildings
    archivedBuildings.splice(buildingIndex, 1);
    saveArchivedBuildings(archivedBuildings);
    
    // Add to active buildings
    const buildings = getBuildings();
    buildings.push(unarchived);
    saveBuildings(buildings);
    
    return unarchived;
  } catch (error) {
    console.error('Error unarchiving building:', error);
    return null;
  }
};

export const updateApartment = (
  buildingId: string,
  floorId: string,
  apartmentId: string,
  updates: Partial<Apartment>,
  archived = false
): Apartment => {
  try {
    const buildings = archived ? getArchivedBuildings() : getBuildings();
    const buildingIndex = buildings.findIndex(b => b.id === buildingId);
    
    if (buildingIndex === -1) {
      throw new Error('Gebäude nicht gefunden');
    }
    
    const building = buildings[buildingIndex];
    const floorIndex = building.floors.findIndex(f => f.id === floorId);
    
    if (floorIndex === -1) {
      throw new Error('Etage nicht gefunden');
    }
    
    const floor = building.floors[floorIndex];
    const apartmentIndex = floor.apartments.findIndex(a => a.id === apartmentId);
    
    if (apartmentIndex === -1) {
      throw new Error('Wohnung nicht gefunden');
    }
    
    // Handle Konzern ID assignment or removal
    if ('konzernId' in updates) {
      const oldKonzernId = floor.apartments[apartmentIndex].konzernId;
      const newKonzernId = updates.konzernId;
      
      // If there was a previous Konzern ID, release it
      if (oldKonzernId) {
        releaseKonzernId(oldKonzernId);
      }
      
      // If there's a new Konzern ID, assign it
      if (newKonzernId) {
        assignKonzernId(newKonzernId, apartmentId, buildingId);
      }
    }
    
    // Create history entry for status change
    if ('status' in updates && updates.status !== floor.apartments[apartmentIndex].status) {
      const currentUser = getCurrentUser();
      const historyEntry = {
        id: crypto.randomUUID(),
        timestamp: new Date(),
        userId: currentUser?.id || 'system',
        userName: currentUser?.name || 'System',
        action: 'status_change' as const,
        oldValue: floor.apartments[apartmentIndex].status,
        newValue: updates.status!,
        notes: updates.notes
      };
      
      updates.history = [
        ...(floor.apartments[apartmentIndex].history || []),
        historyEntry
      ];
    }
    
    const updatedApartment = {
      ...floor.apartments[apartmentIndex],
      ...updates,
      lastUpdated: new Date()
    };
    
    floor.apartments[apartmentIndex] = updatedApartment;
    building.floors[floorIndex] = floor;
    buildings[buildingIndex] = building;
    
    if (archived) {
      saveArchivedBuildings(buildings);
    } else {
      saveBuildings(buildings);
    }
    
    return updatedApartment;
  } catch (error) {
    console.error('Error updating apartment:', error);
    throw error;
  }
};

export const getBuildingCompletionPercentage = (building: Building): number => {
  const totalApartments = building.floors.reduce(
    (sum, floor) => sum + floor.apartments.length, 
    0
  );
  
  if (totalApartments === 0) return 0;
  
  const completedApartments = building.floors.reduce(
    (sum, floor) => sum + floor.apartments.filter(
      apt => ['completed', 'completed_with_cable', 'completed_no_measurement'].includes(apt.status)
    ).length, 
    0
  );
  
  return Math.round((completedApartments / totalApartments) * 100);
};

export const getKonzernIds = (): KonzernId[] => {
  try {
    const konzernIdsJson = localStorage.getItem(KONZERN_IDS_KEY);
    if (!konzernIdsJson) return [];
    
    return JSON.parse(konzernIdsJson, (key, value) => {
      if ((key === 'assignedAt' || key === 'releasedAt') && value) {
        return new Date(value);
      }
      return value;
    });
  } catch (error) {
    console.error('Error reading Konzern IDs from localStorage:', error);
    return [];
  }
};

export const getAvailableKonzernIds = (): KonzernId[] => {
  try {
    const konzernIds = getKonzernIds();
    return konzernIds.filter(kid => !kid.isUsed);
  } catch (error) {
    console.error('Error getting available Konzern IDs:', error);
    return [];
  }
};

const saveKonzernIds = (konzernIds: KonzernId[]): void => {
  try {
    localStorage.setItem(KONZERN_IDS_KEY, JSON.stringify(konzernIds));
  } catch (error) {
    console.error('Error saving Konzern IDs to localStorage:', error);
  }
};

export const addKonzernIds = (values: string[]): KonzernId[] => {
  try {
    const konzernIds = getKonzernIds();
    const currentUser = getCurrentUser();
    const organizationId = currentUser?.organizationId || 'default-org';
    
    // Filter out values that already exist
    const newValues = values.filter(value => 
      !konzernIds.some(kid => kid.value === value)
    );
    
    // Create new Konzern IDs
    const newKonzernIds = newValues.map(value => ({
      id: crypto.randomUUID(),
      organizationId,
      value,
      isUsed: false
    }));
    
    // Add new Konzern IDs to the list
    const updatedKonzernIds = [...konzernIds, ...newKonzernIds];
    saveKonzernIds(updatedKonzernIds);
    
    return newKonzernIds;
  } catch (error) {
    console.error('Error adding Konzern IDs:', error);
    throw error;
  }
};

export const assignKonzernId = (konzernIdValue: string, apartmentId: string, buildingId: string): KonzernId => {
  try {
    const konzernIds = getKonzernIds();
    const konzernIdIndex = konzernIds.findIndex(kid => kid.value === konzernIdValue);
    
    if (konzernIdIndex === -1) {
      // If the Konzern ID doesn't exist yet, create it
      const newKonzernIds = addKonzernIds([konzernIdValue]);
      
      if (newKonzernIds.length === 0) {
        throw new Error('Konzern ID konnte nicht erstellt werden');
      }
      
      // Now assign the newly created Konzern ID
      return assignKonzernId(konzernIdValue, apartmentId, buildingId);
    }
    
    // If the Konzern ID is already assigned to this apartment, just return it
    if (konzernIds[konzernIdIndex].isUsed && 
        konzernIds[konzernIdIndex].apartmentId === apartmentId &&
        konzernIds[konzernIdIndex].buildingId === buildingId) {
      return konzernIds[konzernIdIndex];
    }
    
    // If the Konzern ID is already used elsewhere, throw an error
    if (konzernIds[konzernIdIndex].isUsed) {
      throw new Error('Konzern ID wird bereits verwendet');
    }
    
    const updatedKonzernId = {
      ...konzernIds[konzernIdIndex],
      isUsed: true,
      apartmentId,
      buildingId,
      assignedAt: new Date()
    };
    
    konzernIds[konzernIdIndex] = updatedKonzernId;
    saveKonzernIds(konzernIds);
    
    return updatedKonzernId;
  } catch (error) {
    console.error('Error assigning Konzern ID:', error);
    throw error;
  }
};

export const releaseKonzernId = (konzernIdValue: string): KonzernId => {
  try {
    const konzernIds = getKonzernIds();
    const konzernIdIndex = konzernIds.findIndex(kid => kid.value === konzernIdValue);
    
    if (konzernIdIndex === -1) {
      throw new Error('Konzern ID nicht gefunden');
    }
    
    const updatedKonzernId = {
      ...konzernIds[konzernIdIndex],
      isUsed: false,
      apartmentId: undefined,
      buildingId: undefined,
      releasedAt: new Date()
    };
    
    konzernIds[konzernIdIndex] = updatedKonzernId;
    saveKonzernIds(konzernIds);
    
    return updatedKonzernId;
  } catch (error) {
    console.error('Error releasing Konzern ID:', error);
    throw error;
  }
};

export const deleteKonzernId = (konzernIdValue: string): void => {
  try {
    const konzernIds = getKonzernIds();
    const konzernIdIndex = konzernIds.findIndex(kid => kid.value === konzernIdValue);
    
    if (konzernIdIndex === -1) {
      throw new Error('Konzern ID nicht gefunden');
    }
    
    if (konzernIds[konzernIdIndex].isUsed) {
      throw new Error('Konzern ID wird verwendet und kann nicht gelöscht werden');
    }
    
    konzernIds.splice(konzernIdIndex, 1);
    saveKonzernIds(konzernIds);
  } catch (error) {
    console.error('Error deleting Konzern ID:', error);
    throw error;
  }
};

export const importKonzernIdsFromExcel = async (file: File): Promise<KonzernId[]> => {
  try {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          if (!e.target?.result) {
            reject(new Error('Keine Daten in der Datei gefunden'));
            return;
          }
          
          // Parse CSV data (Excel files can be saved as CSV)
          const text = e.target.result.toString();
          const lines = text.split('\n');
          
          // Extract values (assuming one value per line)
          const values = lines
            .map(line => line.trim())
            .filter(line => line.length > 0);
          
          // Add Konzern IDs
          const newKonzernIds = addKonzernIds(values);
          resolve(newKonzernIds);
        } catch (error) {
          reject(error);
        }
      };
      
      reader.onerror = () => {
        reject(new Error('Fehler beim Lesen der Datei'));
      };
      
      reader.readAsText(file);
    });
  } catch (error) {
    console.error('Error importing Konzern IDs from Excel:', error);
    throw error;
  }
};

// Initialize storage on import
try {
  initializeLocalStorage();
} catch (error) {
  console.error('Failed to initialize local storage:', error);
}